import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Collapse,
  Link,
  Card,
  CardContent,
} from '@mui/material'
import { KeyRound, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react'

interface YouTubeApiKeySetupProps {
  mediaAccountId: number
  existingApiKey?: boolean  // Just a boolean, never expose the actual key
  onSave: (mediaAccountId: number, apiKey: string) => Promise<void>
}

export default function YouTubeApiKeySetup({ 
  mediaAccountId,
  existingApiKey, 
  onSave 
}: YouTubeApiKeySetupProps) {
  const [apiKey, setApiKey] = useState('')
  const [showInstructions, setShowInstructions] = useState(!existingApiKey)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Please enter a valid API key')
      return
    }

    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      await onSave(mediaAccountId, apiKey.trim())
      setSuccess(true)
      setApiKey('') // Clear the input after successful save
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save API key')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Header Card */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(219, 39, 119, 0.1) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          mb: 3,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <KeyRound size={24} color="white" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                YouTube API Key Setup
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Enable comment automation with your own API quota
              </Typography>
            </Box>
          </Box>

          {existingApiKey && (
            <Alert 
              icon={<CheckCircle size={20} />}
              severity="success"
              sx={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                color: '#22c55e',
                border: '1px solid rgba(34, 197, 94, 0.2)',
              }}
            >
              API Key configured - Your YouTube automations are active
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Instructions Section */}
      <Card
        sx={{
          background: 'rgba(30, 30, 30, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          mb: 3,
        }}
      >
        <CardContent>
          <Button
            fullWidth
            onClick={() => setShowInstructions(!showInstructions)}
            sx={{
              justifyContent: 'space-between',
              color: 'white',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              p: 0,
            }}
            endIcon={showInstructions ? <ChevronUp /> : <ChevronDown />}
          >
            How to Get Your YouTube API Key
          </Button>

          <Collapse in={showInstructions}>
            <Box sx={{ mt: 3 }}>
              <Alert 
                severity="info"
                icon={<AlertTriangle size={20} />}
                sx={{ 
                  mb: 3,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                }}
              >
                You&apos;ll get 10,000 API units per day - enough for ~200 automated comment replies
              </Alert>

              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Step-by-Step Instructions:
              </Typography>

              <Box component="ol" sx={{ pl: 3, '& li': { mb: 2, color: 'rgba(255, 255, 255, 0.8)' } }}>
                <li>
                  <Typography>
                    Go to{' '}
                    <Link
                      href="https://console.cloud.google.com/"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        color: '#9333ea',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      Google Cloud Console
                      <ExternalLink size={14} />
                    </Link>
                  </Typography>
                </li>

                <li>
                  <Typography>
                    <strong>Create a new project</strong> (or select an existing one)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 2, mt: 0.5 }}>
                    Click &quot;Select a project&quot; → &quot;New Project&quot; → Name it &quot;Nalapod YouTube&quot;
                  </Typography>
                </li>

                <li>
                  <Typography>
                    <strong>Enable the YouTube Data API v3</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 2, mt: 0.5 }}>
                    Go to &quot;APIs & Services&quot; → &quot;Library&quot; → Search &quot;YouTube Data API v3&quot; → Click &quot;Enable&quot;
                  </Typography>
                </li>

                <li>
                  <Typography>
                    <strong>Create API credentials</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 2, mt: 0.5 }}>
                    Go to &quot;Credentials&quot; → &quot;Create Credentials&quot; → &quot;API Key&quot;
                  </Typography>
                </li>

                <li>
                  <Typography>
                    <strong>Restrict your API key</strong> (Important for security!)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 2, mt: 0.5 }}>
                    • Click &quot;Restrict Key&quot;<br />
                    • Under &quot;API restrictions&quot;, select &quot;Restrict key&quot;<br />
                    • Choose only &quot;YouTube Data API v3&quot;<br />
                    • Save
                  </Typography>
                </li>

                <li>
                  <Typography>
                    <strong>Copy your API key</strong> and paste it below
                  </Typography>
                </li>
              </Box>

              <Alert 
                severity="warning"
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(251, 146, 60, 0.1)',
                  color: '#fb923c',
                  border: '1px solid rgba(251, 146, 60, 0.2)',
                }}
              >
                <strong>Security Note:</strong> Your API key is encrypted and stored securely. Never share it publicly!
              </Alert>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* API Key Input Section */}
      <Card
        sx={{
          background: 'rgba(30, 30, 30, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
        }}
      >
        <CardContent>
          <TextField
            fullWidth
            label="YouTube API Key"
            placeholder="AIzaSy..."
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value)
              setError('')
            }}
            type="password"
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(147, 51, 234, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9333ea',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                '&.Mui-focused': {
                  color: '#9333ea',
                },
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              API Key saved successfully!
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
                handleSave().catch((err) => {
                  console.error('Failed to save API key:', err)
                })
              }}
            disabled={loading || !apiKey.trim()}
            sx={{
              background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              color: 'white',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #be185d 100%)',
              },
              '&:disabled': {
                background: 'rgba(147, 51, 234, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? 'Saving...' : existingApiKey ? 'Update API Key' : 'Save API Key'}
          </Button>
        </CardContent>
      </Card>

      {/* Benefits Section */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          🚀 With your own API key, you get dedicated quota just for your automations
        </Typography>
      </Box>
    </Box>
  )
}